import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/lib/api";
import { formatRut } from "@/lib/rut";
import { handleApiError } from "@/lib/api-error";
import { Upload, Mail, Building, Briefcase, HelpCircle } from "@/components/ds/icons";
import { toast } from "sonner";
import type { DriveStep } from "driver.js";
import { PageHeader } from "@/components/ui/PageHeader";
import { useGuide } from "@/hooks/useGuide";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";

function buildPerfilGuideSteps(): DriveStep[] {
  return [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "Tu cargo en la empresa",
        description:
          "Asigná tu cargo dentro de la empresa para que el sistema pueda asociarte correctamente a los contratos y documentos de prevención.",
      },
    },
    // Paso 2 — Highlight del campo de cargo
    {
      element: '[data-tour="perfil-cargo"]',
      popover: {
        title: "Cargo en la empresa",
        description:
          "Tu cargo define tu rol dentro de la empresa contratante. Aparecerá en los documentos de prevención y en los reportes. Puedes actualizar tu cargo desde la configuración de usuarios o contactando al administrador de tu empresa.",
      },
    },
    // Paso 3 — Popover de cierre (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en el botón de ayuda en la parte superior de la página.",
      },
    },
  ];
}

export function Perfil() {
  const { user, sessions, activeEmpresaId, refreshUser } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
  });

  useEffect(() => {
    if (user) {
      setFormData({ name: user.name, email: user.email });
    }
  }, [user]);

  const empresaNombre = user?.empresa?.nombre ?? "";

  // Cargo de la sesión activa (viene del WebSession del backend)
  const activeSession = sessions.find(
    (s) => s.empresa?.id === activeEmpresaId || s.is_super_admin,
  );
  const cargoNombre = activeSession?.cargo?.nombre ?? null;

  // --- Guía de Perfil ---
  const buildSteps = useCallback(buildPerfilGuideSteps, []);
  const { startGuide } = useGuide("guide-perfil", { buildSteps });
  usePendingGuide("guide-perfil", startGuide, { anchorSelector: '[data-tour="perfil-cargo"]' });
  useGuideAutostart("guide-perfil", startGuide, { anchorSelector: '[data-tour="perfil-cargo"]' });
  // --- fin Guía de Perfil ---

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("El nombre y el correo son obligatorios");
      return;
    }

    setIsSaving(true);
    try {
      await api.put("/me/perfil", { name: formData.name, email: formData.email });
      await refreshUser();
      toast.success("Perfil actualizado correctamente");
      setIsEditing(false);
    } catch (err) {
      handleApiError(err, "No se pudo actualizar el perfil");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({ name: user.name, email: user.email });
    }
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mi Perfil"
        subtitle="Tu nombre, tu correo y tu contraseña. El RUT y el cargo los define tu empresa."
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            onClick={startGuide}
            aria-label="Ver guía de perfil"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Foto de Perfil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-primary text-primary-foreground mx-auto">
              <span className="text-4xl font-bold">{user?.iniciales ?? "—"}</span>
            </div>
            <Button variant="outline" className="w-full" disabled>
              <Upload className="mr-2 h-4 w-4" />
              Cambiar Foto
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Formato PNG o JPG. Máximo 5MB
            </p>
          </CardContent>
        </Card>

        <Card className="md:col-span-2 hover:shadow-md transition-shadow">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Información Personal</CardTitle>
                <CardDescription>Tus datos de cuenta</CardDescription>
              </div>
              {isEditing ? (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isSaving}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? "Guardando…" : "Guardar"}
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Nombre Completo
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing || isSaving}
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4" />
                  Correo Electrónico
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing || isSaving}
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="flex items-center gap-2 text-sm font-medium">
                  <Building className="h-4 w-4" />
                  Empresa
                </Label>
                <Input
                  id="company"
                  value={empresaNombre || "—"}
                  disabled
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rut" className="flex items-center gap-2 text-sm font-medium">
                  <Briefcase className="h-4 w-4" />
                  RUT
                </Label>
                <Input
                  id="rut"
                  value={user?.rut ? formatRut(user.rut) : "—"}
                  disabled
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2" data-tour="perfil-cargo">
                <Label htmlFor="cargo" className="flex items-center gap-2 text-sm font-medium">
                  <Briefcase className="h-4 w-4" />
                  Cargo en la empresa
                </Label>
                <Input
                  id="cargo"
                  value={cargoNombre ?? "Sin cargo asignado"}
                  disabled
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">
                  Tu cargo define tu rol en los documentos de prevención. Para modificarlo, contacta al administrador de tu empresa.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle>Seguridad</CardTitle>
          <CardDescription>Gestiona la seguridad de tu cuenta</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="font-medium">Cambiar Contraseña</p>
              <p className="text-sm text-muted-foreground">Actualiza tu contraseña regularmente</p>
            </div>
            <PasswordChangeDialog />
          </div>

          <Separator />

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="font-medium">Autenticación de Dos Factores</p>
              <p className="text-sm text-muted-foreground">Añade una capa extra de seguridad</p>
            </div>
            <Button variant="outline" disabled>Configurar</Button>
          </div>

          <Separator />

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="font-medium">Sesiones Activas</p>
              <p className="text-sm text-muted-foreground">Gestiona tus sesiones en otros dispositivos</p>
            </div>
            <Button variant="outline" disabled>Ver</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PasswordChangeDialog() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setPassword("");
    setPasswordConfirmation("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !password) {
      toast.error("Completa todos los campos");
      return;
    }
    if (password.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== passwordConfirmation) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setIsSaving(true);
    try {
      await api.put("/me/perfil/password", {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      });
      toast.success("Contraseña actualizada correctamente");
      handleOpenChange(false);
    } catch (err) {
      handleApiError(err, "No se pudo actualizar la contraseña");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button variant="outline" onClick={() => setOpen(true)}>
        Cambiar
      </Button>
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
            <DialogDescription>
              Ingresa tu contraseña actual y la nueva. Las demás sesiones se cerrarán.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
          <div className="space-y-2">
            <Label htmlFor="current_password">Contraseña actual</Label>
            <Input
              id="current_password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_password">Nueva contraseña</Label>
            <Input
              id="new_password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              disabled={isSaving}
            />
            <p className="text-xs text-muted-foreground">Mínimo 8 caracteres</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_password_confirmation">Confirmar nueva contraseña</Label>
            <Input
              id="new_password_confirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              autoComplete="new-password"
              disabled={isSaving}
            />
          </div>

          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Guardando…" : "Actualizar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

